<template>
  <div class="format" @click="onTemplateAreaClick">
    <div class="renew3_wrapper format_wrapper">
      <top-popular-formats />

      <top-recommended-formats />
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * トップページのテンプレート選択（書式一覧）。トップで一番大きいブロック。
 *
 * 中身は「人気」「おすすめ」の2ブロックに割ってあり、ここはその枠と
 * カード群のクリックを1箇所で受けるデリゲーションだけを持つ。
 *
 * ⚠️ 静的トップ（public/top-content.html）にも焼かれる。ビルド時に
 * scripts/extract-top-static.js が <top-format-list /> をこの <template> で
 * 置き換え、さらに中の子タグも再帰展開する。動的な値を足すときは Top.vue 側で
 * <!-- static:skip --> に入れること。
 *
 * スタイルは子の中の要素にも当てる必要があるので :deep() を使う
 * （.format_link 等は「人気」「おすすめ」で共通のため、子に分けず親へ集約）。
 */
import { useRouter } from 'vue-router/composables'
import { useAuth } from '@/composables/useAuth'
import TopPopularFormats from '@/components/top/TopPopularFormats.vue'
import TopRecommendedFormats from '@/components/top/TopRecommendedFormats.vue'

const { isAuthenticated } = useAuth()
const router = useRouter()

/**
 * テンプレートCTA（人気 + おすすめ）の href は作成画面（/edit/<先頭カード>）。
 * 未ログインはそのまま扉ページを飛ばし、ログイン済みだけ「保存データを使う」のある
 * 扉ページへ寄せる。静的トップは href をそのまま使うので href は未ログイン向けの
 * 静的値にしておく（:href にすると静的側が死ぬ）。
 */
function onTemplateAreaClick(event: MouseEvent): void {
  if (!isAuthenticated.value || event.metaKey || event.ctrlKey) return
  const target = event.target as HTMLElement | null
  const el = target?.closest('a[href^="/format/"][href*="/edit/"]')
  if (!el) return
  event.preventDefault()
  const formatId = (el.getAttribute('href') || '').split('/')[2]
  router.push({ name: 'formatDetail', params: { formatId } })
}
</script>

<style lang="scss" scoped>
// max-width() ミックスインの定義元。Top.vue から切り出したスタイルが使っている
@import '@/assets/scss/variables';

/* テンプレート選択 */
.renew3_top .format {
  margin-bottom: 60px;
}

.format :deep(.format_block) {
  margin-bottom: 30px;
}

.format :deep(.format_block_h2) {
  font-weight: bold;
  font-size: 20px;
  margin-bottom: 8px;
  text-align: left;
  color: #404040;
}

.format :deep(.format_block_h2_2) {
  margin-bottom: 0;
}

.format :deep(.format_block_lead) {
  font-size: 14px;
  margin-bottom: 20px;
}

.format :deep(.format_block_flex) {
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 20px;
}

.format :deep(.format_more) {
  text-align: right;
  margin: 16px 0 8px;
}

.format :deep(.format_link) {
  width: calc(25% - 15px);
  display: flex;
  flex-direction: column;
}

.format :deep(.format_link):hover {
  transform: scale(1.04);
  text-decoration: none;
  opacity: 1;
}

.format :deep(.format_link_top) {
  position: relative;
  text-align: center;
  background: #eef5e8;
  border-radius: 5px 5px 0 0;
  height: 100px;
  overflow: hidden;
}

.format :deep(.format_link_top2) {
  background: #fef5f1;
}

.format :deep(.format_link_ocr) {
  position: relative;
  display: flex;
}

.format :deep(.format_link_outline) {
  border: 1px solid #aac79a;
  border-radius: 5px;
  overflow: hidden;
}

.format :deep(.format_link_outline) .format_link_top,
.format :deep(.format_link_outline) .format_link_bottom {
  background: transparent;
}

.format :deep(.format_link_outline) .format_btn {
  background: #0f9934;
  color: #fff;
}

.format :deep(.ocr_label) {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px;
  border: 2px dotted #aac79a;
  border-radius: 5px;
  background: #eef5e8;
  color: #004d40;
  font-weight: bold;
  font-size: 16px;
  line-height: 1.6;
  text-align: center;
}

.format :deep(.ocr_label_sub) {
  margin-top: 10px;
  font-size: 10px;
  font-weight: normal;
  color: #999;
  line-height: 1.4;
}

.format :deep(.youtube-card) {
  .format_link_top {
    height: 150px;
    padding: 15px 10px;
    background: #fbeaed;

    @include max-width(sm) {
      height: 170px;
    }

    .format_link_memo {
      color: #e3165c;
      border: 1px solid #e3165c;
    }

    .format_link_img {
      width: 100%;
      padding-top: 0;
    }

    .sp {
      display: none;

      @include max-width(sm) {
        display: block;
        height: 100%;
        object-fit: cover;
      }
    }
  }

  .format_btn {
    background: #e3165c;
  }
}

.format :deep(.format_link_memo) {
  position: absolute;
  display: inline-block;
  font-weight: bold;
  font-size: 10px;
  color: #e95b10;
  background: #fff;
  border: 1px solid #e95b10;
  padding: 0 10px;
  margin-right: 5px;
  border-radius: 25px;
  top: 5px;
  left: 5px;
}

.format :deep(.format_link_img) {
  display: block;
  width: 90%;
  padding-top: 15px;
  margin: 0 auto;
}

/* 横長の見本画像（宛名L版シール）。幅基準だと上段の高さで下半分が切れるため、高さに収める */
.format :deep(.format_link_img_landscape) {
  box-sizing: border-box;
  width: auto;
  max-width: 90%;
  height: 100%;
  padding: 24px 0 8px;
  object-fit: contain;
}

.format :deep(.format_link_bottom) {
  background: #eef5e8;
  padding: 14px;
  height: 80px;
}

.format :deep(.format_link_bottom2) {
  background: #fef5f1;
}

.format :deep(.format_link_h3) {
  font-weight: bold;
  font-size: 16px;
  color: #004d40;
}

.format :deep(.format_link_txt) {
  font-size: 12px;
}

.format :deep(.format_btn) {
  width: 100%;
  padding: 10px;
  border: none;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  background: #0f9934;
  border-radius: 0 0 4px 4px;
  margin-top: auto;
}

.format :deep(.format_btn):hover {
  opacity: 1;
}

.format :deep(.format_btn2) {
  background: #e95b10;
}

.format :deep(.format_btn3) {
  background: #004d40;
}

/* SP */
@media screen and (width <= 640px) {
  /* フォーマット選択 */
  .format :deep(.format_block_flex) {
    gap: 10px;
  }

  .format :deep(.format_link) {
    width: calc(50% - 5px);
  }

  .format :deep(.ocr_label) {
    font-size: 13px;
  }

  .format :deep(.format_link_bottom1) {
    height: 90px;
  }

  .format :deep(.format_link_h3) {
    font-size: 14px;
  }

  .format :deep(.format_btn) {
    font-size: 14px;
  }

  .format :deep(.format_link_h3big) {
    font-size: 16px;
  }
}
</style>
